import React, { useState } from 'react';

import { IVideoWith5050TextFields } from '../../../types/contentful';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';

import { trackWithFingerprint } from '../../utils/trackWithFingerprint';

import RichText from '../RichText';
import { Lightbox } from '../VideoBlock/Lightbox';
import cx from 'classnames';
import {
  EmbeddedEntryNode,
  EmbeddedInlineEntryNode,
  HyperlinkNode,
  ParagraphNode
} from '../RichText/Nodes';

import { PlayIcon, VideoButton, VideoWrapper } from './styles';
import { Eyebrow } from '../Eyebrow';
import { Asset } from '../AtomicComponents/Asset';

interface Props extends IVideoWith5050TextFields {
  sysId?: string;
}

export function VideoWith5050Text({
  superheading,
  headingText,
  bodyCopy,
  videoAsset,
  videoAssetBackups,
  assetFirst,
  vimeoId,
  videoTitle,
  backgroundColor,
  roundedCorners,
  sysId
}: Props) {
  const [showingVideo, setShowingVideo] = useState(false);
  const contentType = videoAsset?.fields.file.contentType;

  const RICH_TEXT_OVERRIDE: RenderNode = {
    [INLINES.HYPERLINK]: (node, children) => (
      <HyperlinkNode node={node} colour="inlineAnchor">
        {children}
      </HyperlinkNode>
    ),
    [INLINES.EMBEDDED_ENTRY]: (node, children) => (
      <EmbeddedInlineEntryNode
        node={node}
        colour="inlineAnchor"
        fontWeight="inlineAnchor"
      >
        {children}
      </EmbeddedInlineEntryNode>
    ),
    [BLOCKS.EMBEDDED_ENTRY]: (node, children) => (
      <div className="[&_div]:m-0 [&_div]:w-full [&_div]:pl-0">
        <EmbeddedEntryNode node={node} colour={'defaultText'}>
          {children}
        </EmbeddedEntryNode>
      </div>
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <ParagraphNode node={node} className="!text-base">
        {children}
      </ParagraphNode>
    )
  };

  const toggleVideo = (displayVideo: boolean) => {
    setShowingVideo(displayVideo);
    if (displayVideo) {
      trackWithFingerprint('Video Opened', {
        page_url: window.location.href
      });
    } else {
      trackWithFingerprint('Video Closed', {
        page_url: window.location.href
      });
      document.getElementById('gl-video-play-button')?.focus();
    }
  };

  return (
    <div
      className={cx('block-container', {
        'py-5 tablet:py-10 desktop:py-15': backgroundColor
      })}
      data-blockid="videoWith5050Text"
      data-testid="videoWith5050Text"
      id={sysId}
    >
      <div
        className={cx(
          'layout flex-col items-center !py-0 tablet:flex-row [&>div]:tablet:px-3 [&>div]:desktop:px-4',
          {
            'tablet:flex-row': assetFirst,
            'tablet:flex-row-reverse': !assetFirst
          }
        )}
        style={{
          backgroundColor: backgroundColor ?? 'transparent'
        }}
      >
        <div className="column w-full tablet:w-1/2">
          {!videoAsset && vimeoId && (
            <VideoWrapper data-blockid="videoWrapper">
              <VideoButton
                id="gl-video-play-button"
                onClick={(e) => {
                  toggleVideo(true);
                  e.currentTarget.blur();
                }}
                aria-label="Play video"
                data-testid="videoWith5050Text-button"
              >
                <PlayIcon
                  width="221"
                  height="221"
                  viewBox="0 0 221 221"
                  role="img"
                >
                  <path
                    className="playIconBackground"
                    d="M0 56C0 25.072 25.0721 0 56 0H164.482C195.41 0 220.482 25.0721 220.482 56V164.482C220.482 195.41 195.41 220.482 164.482 220.482H56C25.072 220.482 0 195.41 0 164.482V56Z"
                    fill="#035C67"
                  />
                  <path
                    className="playIconCenter"
                    d="M145.086 102.266C150.419 105.345 150.419 113.043 145.086 116.123L97.2475 143.742C91.9141 146.821 85.2474 142.972 85.2474 136.814L85.2475 81.5748C85.2475 75.4164 91.9141 71.5674 97.2475 74.6466L145.086 102.266Z"
                    fill="#80D6B0"
                  />
                </PlayIcon>
              </VideoButton>
              {!videoAsset && vimeoId && videoTitle && (
                <Lightbox
                  title={videoTitle}
                  showingVideo={showingVideo}
                  setShowingVideo={toggleVideo}
                  vimeoId={vimeoId}
                />
              )}
            </VideoWrapper>
          )}
          {videoAsset && contentType?.includes('video') && (
            <Asset
              asset={videoAsset}
              videobackups={videoAssetBackups}
              className={cx({
                'rounded-3xl tablet:rounded-4xl': roundedCorners
              })}
              loading="lazy"
              muted
            />
          )}
        </div>
        <div className="column mt-6 flex w-full justify-center tablet:mt-0 tablet:w-1/2">
          <div className="border-box flex w-full flex-col justify-center tablet:w-auto tablet:max-w-[480px] tablet:flex-1">
            {superheading && <Eyebrow eyebrowFields={superheading.fields} />}
            <div className="border-box mb-3">
              <h2
                className="heading-2"
                data-blockid="videoWith5050Text-headline"
              >
                {headingText}
              </h2>
            </div>
            {bodyCopy && (
              <div
                className="mb-8 [&_p]:!text-base"
                data-blockid="videoWith5050Text-subhead"
              >
                <RichText
                  data={bodyCopy}
                  overrideOptions={RICH_TEXT_OVERRIDE}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
